import { CSSProperties, KeyboardEvent, ReactNode } from 'react'

import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material'
import { Stack, TableCell, TableRow } from '@mui/material'
import { VirtualItem, Virtualizer } from '@tanstack/react-virtual'

import './SectionHeaderRow.scss'

/**
 * Component representing a full width section header row in a virtualized table.
 * Used for visually grouping rows into sections (see getRowSectionId and renderSectionHeader props of GRTable).
 * When collapsible is set, the row acts as an accordion toggle for the rows belonging to its section.
 */

type SectionHeaderRowProps = {
  virtualRow: VirtualItem & { style?: CSSProperties }
  virtualizer: Virtualizer<Window, Element> | Virtualizer<Element, Element> | Virtualizer<HTMLElement, Element>
  colSpan: number
  children?: ReactNode
  collapsible?: boolean
  collapsed?: boolean
  onToggle?: () => void
}

export const SectionHeaderRow = ({ virtualRow, virtualizer, colSpan, children, collapsible, collapsed, onToggle }: SectionHeaderRowProps) => {
  const handleKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
    if (!onToggle) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onToggle()
    }
  }

  return (
    <TableRow
      className="SectionHeaderRow"
      data-index={virtualRow.index}
      ref={(node) => virtualizer.measureElement(node)}
      style={{
        position: 'absolute',
        top: virtualRow.start - virtualizer.options.scrollMargin,
      }}
      hover={collapsible}
      onClick={onToggle}
      onKeyDown={handleKeyDown}
      role={collapsible ? 'button' : undefined}
      tabIndex={collapsible ? 0 : undefined}
      aria-expanded={collapsible ? !collapsed : undefined}
      sx={{ cursor: collapsible ? 'pointer' : undefined }}
    >
      <TableCell className="SectionHeaderRow__cell" colSpan={colSpan}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          {children}
          {collapsible && (collapsed ? <KeyboardArrowRight fontSize="small" /> : <KeyboardArrowDown fontSize="small" />)}
        </Stack>
      </TableCell>
    </TableRow>
  )
}
